import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ShiftExpectedResponse } from '@/api/handlers/resources/shifts/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { getUser } from '@/utils/authSession';
import { ShiftQueryKeys } from './shiftQueryKeys';

/**
 * Server-authoritative expected cash for an `open` or `close` count. Fetched
 * when the shift modal opens (the value stays hidden behind the blind count
 * until the seller reveals it). `staleTime` is 0 so the close-side figure tracks
 * cash sales taken since the shift opened. The key carries the signed-in
 * seller, whose drawer the figure is. A refused read (`NO_OPEN_SHIFT` when the
 * shift was closed elsewhere) re-reads the current shift once it lands, so the
 * modal's mode follows the server (SEL-27).
 */
export const useExpectedCashQuery = (
    type: 'open' | 'close',
    enabled = true,
) => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();
    const sellerId = getUser()?.id ?? null;

    const query = useQuery<ApiResponse<ShiftExpectedResponse>>({
        queryKey: ShiftQueryKeys.expected(type, sellerId),
        queryFn: () => apiClient.shifts.getExpected(type),
        enabled,
        staleTime: 0,
    });

    const refused = query.data?.success === false;
    useEffect(() => {
        if (refused) {
            void queryClient.invalidateQueries({
                queryKey: ShiftQueryKeys.current(),
            });
        }
    }, [refused, query.dataUpdatedAt, queryClient]);

    return query;
};
